import { useCallback, useMemo, type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Sidebar } from '@/components/shell/Sidebar';
import {
  useSetBreadcrumbs,
  type BreadcrumbItem,
} from '@/components/shell/ShellContext';
import { DeleteRecordButton } from '@/features/delete/DeleteRecordButton';
import { FAMILY_RECORD_SECTIONS } from '@/features/family-record/sections.config';
import { familyQueries } from '@/features/family-record/queries';
import { AutosaveToggle } from '@/features/patient-record/components/_shared/AutosaveToggle';
import { SaveButton } from '@/features/patient-record/components/_shared/SaveButton';
import { UnsavedChangesDialog } from '@/features/patient-record/components/_shared/UnsavedChangesDialog';
import { useUnsavedNavGuard } from '@/features/patient-record/hooks/useUnsavedNavGuard';
import type { FamilyViewModel } from '@/models/family';

import { FamilyIdentityBand } from './FamilyIdentityBand';
import { FamilySidePanels } from './FamilySidePanels';
import { familySidebarGroups } from './familySidebar';

interface FamilyRecordViewProps {
  readonly family: FamilyViewModel;
}

const DEFAULT_SECTION = FAMILY_RECORD_SECTIONS[0];

/**
 * Loaded-state view for the family record. Mirrors `PatientRecordView`'s
 * shell: sidebar selects a section via `#section-id` hash, only the
 * active section renders, two-column main with a 320px side rail.
 *
 * Members are loaded by an independent query so a failure on
 * `/families/{guid}/members` only degrades the Members card / proband
 * readouts — the rest of the record still renders.
 */
export function FamilyRecordView({ family }: FamilyRecordViewProps): ReactElement {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const membersQuery = useQuery(familyQueries.members(family.guid));

  const onFamilyDeleted = useCallback(() => {
    void navigate('/browse/families');
  }, [navigate]);

  const mergedFamily = useMemo<FamilyViewModel>(() => {
    if (membersQuery.data === undefined) return family;
    const members = membersQuery.data;
    // The detail response (`GET /families/{guid}`) carries no proband_guid and no
    // embedded members, so `family.probandGuid` derived by the mapper is null here.
    // Re-derive it from the separately-loaded members (the PROBAND row is authoritative)
    // so the proband widget + identity band render. PTV2-1157.
    const probandGuid =
      family.probandGuid ??
      members.find((m) => m.cascadeRole === 'PROBAND' && m.personGuid !== null)?.personGuid ??
      null;
    return { ...family, members, probandGuid };
  }, [family, membersQuery.data]);

  const activeId = hash.length > 1 ? hash.slice(1) : (DEFAULT_SECTION?.id ?? '');
  const activeSection =
    FAMILY_RECORD_SECTIONS.find((s) => s.id === activeId) ?? DEFAULT_SECTION;

  const displayName =
    mergedFamily.name.length > 0 ? mergedFamily.name : t('routes.family.unnamedFamily');

  const trail = useMemo<readonly BreadcrumbItem[]>(
    () => [
      { label: t('routes.browseFamilies.heading'), to: '/browse/families' },
      { label: displayName },
    ],
    [t, displayName],
  );
  useSetBreadcrumbs(trail);

  // The family record's cards use the same editable-draft primitive as the patient record, so they
  // need the same three pieces of chrome: a Save/status control, the autosave toggle, and the guard
  // that flushes (or prompts about) unsaved drafts before a section switch or a page exit. Without
  // them an edit on this page had no visible save state and no exit protection (PTV2-1516).
  const navGuard = useUnsavedNavGuard();

  return (
    <div className="flex h-full">
      <Sidebar groups={familySidebarGroups(mergedFamily.guid, t)} />
      <main
        aria-label={t('routes.family.mainAriaLabel', { name: displayName })}
        className="flex-1 flex flex-col min-w-0 min-h-0 overflow-clip"
      >
        <FamilyIdentityBand
          family={mergedFamily}
          actionSlot={
            <>
              <SaveButton scopeId={null} className="mr-1" />
              <AutosaveToggle />
              <DeleteRecordButton
                targetType="FAMILY"
                guid={mergedFamily.guid}
                entityLabel={displayName}
                onDeleted={onFamilyDeleted}
              />
            </>
          }
        />

        <UnsavedChangesDialog
          open={navGuard.blocked}
          canSave={navGuard.canSave}
          summaries={navGuard.summaries}
          onSave={navGuard.onSave}
          onDiscard={navGuard.onDiscard}
          onKeepEditing={navGuard.onKeepEditing}
        />
        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-background"
          aria-label={t('routes.family.recordAriaLabel')}
        >
          <div className="px-8 py-4 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6 max-w-[1400px] mx-auto items-start">
            <div className="space-y-3 min-w-0">{activeSection?.render(mergedFamily)}</div>
            <div className="xl:sticky xl:top-4 xl:self-start xl:max-h-[calc(100vh-6rem)] xl:overflow-y-auto">
              <FamilySidePanels family={mergedFamily} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
